import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BookingErrorCode, BookingValidation, ComponentBase, RentalInfo, RentalStatus } from 't4core';
import * as moment from 'moment';
import { AppIntegrationService, Coordinates, AppSettingsService } from 't4-app-integration';
import { Subscription } from 'rxjs';
import { LocationAccessDialog } from '../../../Rental/dialogs/location-access/location-access';
import { ReturnFarAwayDialog } from '../../../Rental/dialogs/return-faraway/return-faraway';
import { ActiveRentalService, MapService, MapConfig } from 'app-components';

@Component({
    selector: 'app-rental-box',
    templateUrl: './rental-box.component.html',
    styleUrls: ['./rental-box.component.css'],
    standalone: false
})
export class RentalBoxComponent extends ComponentBase implements OnInit, OnDestroy {
  @ViewChild('gmap', { static: false }) gmapElement: any;

  private mapAttached: boolean = false;


  @Input() public booking: RentalInfo;
  @Input() public hasLock: boolean = true;
  @Input() public allowUnmanned: boolean = true;
  @Input() public isNuwiel: boolean = false;
  @Input() public isPickup: boolean = false;


  @Output() public rentalAction = new EventEmitter<string>();
  @Output() public toFarAwayChange = new EventEmitter<boolean>();

  public userLocation: Coordinates = { Latitude: 0, Longitude: 0 };
  public toEarly: boolean = false;
  public toFarAway: boolean = false;
  public late: boolean = false;
  public isVarebil: boolean = false;
  public isElecticBike: boolean = false;
  public isReturn: boolean = false;
  public firstLoad = true;
  public updateInProgress = false;
  public updateInProgressWait = false;
  public locationTries: number = 0;
  public pCountdown: moment.Duration;
  public rCountdown: moment.Duration;
  public available: boolean = false; 
  public distance: number = 0;
  public deviceState: number;
  private locationSubscription;//: Subscription; // current assignment is mysteriously missing a property.
  private timerSubscription: Subscription;

  listener = () => {
    if (!document.hidden) {
      this.updateInProgress = true;
      this.update().then(() =>
        this.updateInProgress = false
      );
    }
  }

  constructor(
    public appService: AppIntegrationService,
    private appSettings: AppSettingsService,
    private activeRentalService: ActiveRentalService,
    private mapService: MapService
  ) {
    super();
  }

  
  async ngOnInit() {
    this.appService.requestDeviceState();

    //if (this.appSettings.meetsMinimumAppVersion(3.1)) {
    //  await this.appService.ensureLocation();
    //} else {
    //  this.appService.Device.Location = 3;
    //}

    if (this.appService.Device.Location < 3) {
      this.Api.post<boolean>("/Rental/LogEvent", "Access to location denied", { rentalId: this.booking.Id });
    }

    this.timerSubscription = this.appService.getSecondTimer().subscribe(x => {
      if (this.appService.Device.Location < 3) this.appService.requestDeviceState();
    });

    this.appService.setLocationUpdateInterval(15000);
    this.locationSubscription = this.appService.locationChanged.subscribe(() => this.callUpdate());

    this.late = this.booking.Status == RentalStatus.Late;

    //Temporarly Solution for Bauhaus Varebil.   As we should not be able to extend them.
    const varebilPrimaryObjectTypes = [657, 873, 874, 876, 933, 950, 415, 1088];
    this.isVarebil = varebilPrimaryObjectTypes.includes(this.booking.PrimaryObjectTypeId);

    const electricBikePrimatyObjecTypes = [851];
    this.isElecticBike = electricBikePrimatyObjecTypes.includes(this.booking.PrimaryObjectTypeId);

    //Update GPS location when user opens app
    document.addEventListener("visibilitychange", this.listener, true);
    //Remove * from reg number if it exists
    if (this.booking && this.booking.RentalObject && this.booking.RentalObject.Title.indexOf('*') == (this.booking.RentalObject.Title.length - 1)) {
      this.booking.RentalObject.Title = this.booking.RentalObject.Title.slice(0, this.booking.RentalObject.Title.indexOf('*'));
    }

    this.isReturn = (this.booking && this.booking.Status == RentalStatus.Active || this.booking.Status == RentalStatus.Late);

    this.secondTick();
    this.minuteTick();

    await this.Isavailable();

    await this.update(true);

  }

  async ngOnDestroy() {
    this.appService.setLocationUpdateInterval(null);
    if (this.locationSubscription) this.locationSubscription.unsubscribe();
    if (this.timerSubscription) this.timerSubscription.unsubscribe();
    document.removeEventListener("visibilitychange", this.listener, true);
    if (this.mapAttached) {
      this.mapService.detachMap();
      this.mapAttached = false;
    }
  }

  public async initMap() {
    if (!this.gmapElement?.nativeElement) return;

    const coors = await this.appService.getCoordinates();
    if (!coors || this._isDestroyed) return;

    const theme = this.appSettings.settings.theme || 'Default';
    const config: MapConfig = {
      userLocation: {
        latitude: coors.Latitude,
        longitude: coors.Longitude
      },
      targetLocation: {
        latitude: this.booking.PickupLocation.TrailerPointLatitude,
        longitude: this.booking.PickupLocation.TrailerPointLongitude,
        radius: this.booking.PickupLocation.TrailerPointRadius
      },
      pinIconUrl: `/Styles/${theme}/Pin.png`
    };

    this.mapService.attachMap(this.gmapElement.nativeElement, config);
    this.mapAttached = true;
    this.mapService.centerMapBetweenPoints(config.userLocation, config.targetLocation);
  }

  private async updateMapMarkers() {
    if (!this.mapAttached) return;

    const coors = await this.appService.getCoordinates();
    if (!coors || this._isDestroyed) return;

    this.mapService.updateUserPosition({
      latitude: coors.Latitude,
      longitude: coors.Longitude
    });
  }


  public async callUpdate() {
    if (this.updateInProgressWait) return;

    this.updateInProgressWait = true;
    await this.update();
    this.updateInProgressWait = false;
  }

  private oldToFarAway:boolean = this.toFarAway;
  public async update(firstLoad = false) {
    //Fetch current current location
    this.updateInProgress = true;
    try {
      this.userLocation = await this.appService.getCoordinates();
      if (this._isDestroyed) return;

      if (this.userLocation == null) {
        //await this.UI.alert("Intellitrailer.Rental.BookingInfo.LocationNotFoundTtl", "Location not found", "Intellitrailer.Rental.BookingInfo.LocationNotFoundMsg", "The app could not fetch your location and will retry again.");
        this.Insights.trackTrace("RentalBoxNoLocation", { IsReturn: this.isReturn.toString()})
        //this.update(firstLoad);
        return;
      }
    }
    catch (ex) {
      //await this.UI.alert("Intellitrailer.Rental.BookingInfo.LocationNotFoundTtl", "Location not found", "Intellitrailer.Rental.BookingInfo.LocationNotFoundMsg", "The app could not fetch your location and will retry again.");
      //this.update(firstLoad);
      return
    }

    //Check if user is to far away to start/return trailer
    if (this.booking.Status == RentalStatus.Active || this.booking.Status == RentalStatus.Late) {
      this.distance = this.appService.getDistance({ Latitude: this.booking.ReturnLocation.TrailerPointLatitude, Longitude: this.booking.ReturnLocation.TrailerPointLongitude }, this.userLocation) * 1000;
      this.toFarAway = this.distance > this.booking.PickupLocation.TrailerPointRadius + 1000;
      this.toFarAwayChange.emit(this.toFarAway);

    }
    else {
      this.distance = this.appService.getDistance({ Latitude: this.booking.PickupLocation.TrailerPointLatitude, Longitude: this.booking.PickupLocation.TrailerPointLongitude }, this.userLocation) * 1000;
      this.toFarAway = this.distance > this.booking.PickupLocation.TrailerPointRadius + 1000;
      this.toFarAwayChange.emit(this.toFarAway);
    }
   
   

    //Override for demo (Set user as at location)
    if (localStorage.getItem('atLocation')) {
      this.toFarAway = localStorage.getItem('atLocation') == "true" ? false : this.toFarAway;
      this.toFarAwayChange.emit(this.toFarAway);
    }

    //Set insights
    if (this.toFarAway != this.oldToFarAway) {
      if (this.toFarAway) {
        setTimeout(() => this.initMap(), 100);
        this.Api.post<boolean>("/Rental/LogEvent", "Left trailer point: " + Math.round(this.distance) + "m, " + JSON.stringify(this.userLocation), { rentalId: this.booking.Id });
      }
      else
        this.Api.post<boolean>("/Rental/LogEvent", "Entered trailer point: " + Math.round(this.distance) + "m", { rentalId: this.booking.Id });

      this.oldToFarAway = this.toFarAway;
    }

    this.updateMapMarkers();
    this.updateInProgress = false;
  }

  public async pickupNow() {
    var ret = await this.UI.confirm("Intellitrailer.Rental.RentalBox.headConfirmEarlyPickup", "Confirm early pickup", "Intellitrailer.Rental.RentalBox.msgConfirmEarlyPickup", "You are to early to pickup this object. If you start the rental now the end time will be moved to maintain the length of the booking. Continue?");
    if (ret) {
      var length = this.booking.ReturnTime.diff(this.booking.PickupTime, 'minutes', true);

      var newStart = moment();
      var newEnd = moment().add(length, 'minutes');

      var validation = await this.Api.put<BookingValidation>("/Rental/ChangeBooking", null,  {
        rentalId: this.booking.Id,
        pickupTime: newStart.toISOString(),
        returnTime: newEnd.toISOString(),
        pickupLocation: this.booking.PickupLocationId,
        returnLocation: this.booking.ReturnLocationId,
        typeId: this.booking.PrimaryObjectTypeId,
        travelRange: this.booking.TravelRange
      });

      if (validation.AggregatedResult == BookingErrorCode.Valid) {
        this.sendRentalAction('start');
      }
    }
  }

  public async Isavailable() {

    this.available = await this.Api.get<boolean>("/Rental/IsAvailable", { rentalId: this.booking.Id }); 
  } 

  async secondTick() {
    if (this.booking && this.booking.Id > 0) {
      this.pCountdown = moment.duration(this.booking.PickupTime.diff(moment()));
      this.rCountdown = moment.duration(this.booking.ReturnTime.diff(moment()));
      this.toEarly = this.pCountdown.asSeconds() > this.Settings.clientSettings.Bookings.EarlyPickupInMinutes * 60;
      this.late = this.rCountdown.asSeconds() < 0;
    }
  }

  public async sendRentalAction(action: string) {
    const requiredLocationCheck = ['start', 'return'].includes(action);
    const returnFarAway = ['return'].includes(action);
    if (requiredLocationCheck && this.appService.Device.Location < 3 && localStorage.getItem('atLocation') != "true") {
      const dialogRef = this.Dialog.open(LocationAccessDialog, {
        disableClose: true,
        width: '350px',
        position: { top: '50px' },
        data: { action }
      });
      dialogRef.afterClosed().subscribe(res => {
        if (res === action) {
          this.rentalAction.emit(action);
        } else {
          this.openSettings();
        }
      })
    }
    else if (returnFarAway && this.toFarAway) {
      const dialogRef = this.Dialog.open(ReturnFarAwayDialog, {
        disableClose: true,
        width: '350px',
        position: { top: '50px' },
        data: {
          location: this.booking.ReturnLocation?.Name,
          distance: await this.appService.getDistanceFromUser(this.booking.PickupLocation.TrailerPointLatitude, this.booking.PickupLocation.TrailerPointLongitude),
          trailerPointLatitude: this.booking.PickupLocation.TrailerPointLatitude,
          trailerPointLongitude: this.booking.PickupLocation.TrailerPointLongitude,
          cordinates: await this.appService.getCoordinates(),
          theme: this.appSettings.settings.theme
        }
      });
      dialogRef.afterClosed().subscribe(x => {
        if (x === action) {
          this.activeRentalService.flagRentalBooking(this.booking.Id);
          this.Insights.trackTrace("Set rental as flagged for returning away from trailer point");
          this.rentalAction.emit(action);
        } else {
          return;
        }
      })
    }
    else {
      this.rentalAction.emit(action);
    }

  }

  public retry() {
    this.deviceState = this.appService.Device.Location;
  }
  public openSettings() {
    this.Api.post<boolean>("/Rental/LogEvent", "Opened device settings to allow location services ", { rentalId: this.booking.Id });
    this.appService.openPermissions();
  }
}
